import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
    Alert,
    Badge,
    Button,
    Card,
    FormCheckbox,
    Input,
    RadioInput,
    Select,
    Switch,
} from "@jhool-io/fe-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import {
    CardTypes,
    CreatedSource,
    IBill,
    IChargePaymentCardPayload,
} from "../../types/billing.types";
import {
    formatDate,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import {
    useFetchPaymentCards,
    useGetOutstandingBills,
} from "../../hooks/billing.queries";
import ListState from "../../../../components/ListState/ListState";
import useCreditCard from "../../../../hooks/useCreditCard";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import { STATES } from "../../../../utils/helpers/us-states/us-states";
import { useChargePaymentCard } from "../../hooks/billing.mutations";
import useToast from "../../../../hooks/useToast";
import { useFetchUpcomingSessions } from "../../../upcoming-sessions/hooks/queries/upcoming-sessions.queries";
import BackLink from "../../../../components/BackLink/BackLink";
import {
    isAcceptJSValidationError,
    useAcceptJS,
} from "../../../../hooks/useAcceptJS/useAcceptJS";

type Option = {
    label: React.ReactNode;
    value: string;
};

const baseSchema = {
    invoice_ids: yup.array(),
    note_type: yup.string(),
    payment_card_id: yup.string(),
    session_id: yup.string(),
    save_card: yup.boolean(),
};

const newCardSchema = yup.object({
    ...baseSchema,
    payment_card: yup.object({
        card_number: yup.string().required("Card number is required"),
        card_cvv: yup
            .string()
            .min(3, "CVV must be at least 3 characters")
            .max(4, "CVV must be at most 4 characters")
            .matches(/^\d+$/, "CVV must contain only numbers")
            .required("Card CVV is required"),
        expiration_date: yup.string().required("Expiration date is required"),
        cardholder_name: yup.string().required("Cardholder name is required"),
        state: yup.string().required("State is required"),
        country: yup.string().required("Country is required"),
        billing_address: yup.string().required("Billing address is required"),
        zip_code: yup.string().required("Zip code is required"),
    }),
});

export default function PaymentForm() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { data, error, isLoading } = useFetchPaymentCards();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { cardImages } = useCreditCard();
    const { mutate: chargeCard, isLoading: chargeCardLoading } =
        useChargePaymentCard();
    const navigate = useNavigate();

    const sessionHistoryId = searchParams.get("sessionHistoryId");

    const invoiceIdsArray = searchParams.getAll("selectedInvoiceIds");
    const invoicePage = searchParams.get("invoicePage");

    const paymentMethodInUrl =
        searchParams.get("payment_method") || "saved_cards";

    const { data: invoicesData, isLoading: invoicesLoading } =
        useGetOutstandingBills({ page: Number(invoicePage) });
    const { data: sessionsData, isLoading: sessionsLoading } =
        useFetchUpcomingSessions(
            sessionHistoryId as string,
            !!sessionHistoryId
        );

    const selectedInvoices =
        invoicesData?.data?.filter((invoice: IBill) =>
            invoiceIdsArray.includes(invoice.invoice_id)
        ) || [];

    const selectedSession =
        sessionHistoryId && sessionsData?.data
            ? sessionsData.data[0]
            : undefined;

    const totalAmount = selectedInvoices.reduce((sum, invoice) => {
        const sessionFee =
            Math.abs(invoice.coinsurance_amount || 0) -
            Math.abs(invoice.coinsurance_amount_collected || 0);

        const insuranceReimbursement =
            Math.abs(invoice.pr100_amount || 0) -
            Math.abs(invoice.pr100_collected || 0);

        const adjustedSessionFee = sessionFee < 0 ? 0 : sessionFee;
        const adjustedInsuranceReimbursement =
            insuranceReimbursement < 0 ? 0 : insuranceReimbursement;

        return sum + adjustedSessionFee + adjustedInsuranceReimbursement;
    }, 0);

    const getClientStateFieldSelectOptions = STATES.map((state) => ({
        label: state.name,
        value: state.abbrv,
    }));

    const savedCardSchema = yup.object({
        ...baseSchema,
        payment_card: yup.object().notRequired(),
    });

    const schema =
        paymentMethodInUrl === "new_card" ? newCardSchema : savedCardSchema;

    const {
        control,
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<IChargePaymentCardPayload>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            payment_card_id: undefined,
            payment_card: {
                country: "USA",
            },
        },
    });

    const { tokenizeCard, isReady } = useAcceptJS();

    const extractFirstName = (fullName = "") =>
        fullName.trim().split(" ").slice(0, -1).join(" ") || fullName.trim();

    const extractLastName = (fullName = "") =>
        fullName.trim().split(" ").slice(-1)[0] || "";

    const setAcceptJSCardErrors = (acceptJSError: unknown) => {
        if (!isAcceptJSValidationError(acceptJSError)) {
            return;
        }

        acceptJSError.issues.forEach((issue) => {
            switch (issue.field) {
                case "card_number":
                    setError("payment_card.card_number", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "expiration_date":
                    setError("payment_card.expiration_date", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "card_cvv":
                    setError("payment_card.card_cvv", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "zip_code":
                    setError("payment_card.zip_code", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "cardholder_name":
                    setError("payment_card.cardholder_name", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                default:
                    toast({ mode: "error", message: issue.message });
            }
        });
    };

    const onSubmit = async (payload: IChargePaymentCardPayload) => {
        const isNewCard = paymentMethodInUrl === "new_card";
        const clientCountry = payload.payment_card?.country ?? "USA";

        let opaqueData:
            Awaited<ReturnType<typeof tokenizeCard>>["opaqueData"] | undefined;

        if (isNewCard) {
            try {
                const expirationDate =
                    payload.payment_card?.expiration_date ?? "";
                const [expirationMonth = "", expirationYear = ""] =
                    expirationDate.split("/");

                const tokenResult = await tokenizeCard({
                    cardNumber: payload.payment_card?.card_number ?? "",
                    cardCode: payload.payment_card?.card_cvv ?? "",
                    month: expirationMonth,
                    year: expirationYear ? `20${expirationYear}` : "",
                    fullName: payload.payment_card?.cardholder_name ?? "",
                    zip: payload.payment_card?.zip_code ?? "",
                });

                opaqueData = tokenResult.opaqueData;
            } catch (acceptJSError) {
                setAcceptJSCardErrors(acceptJSError);
                return;
            }
        }

        const paymentData = {
            amount: sessionHistoryId ? selectedSession?.fee : undefined,
            session_history_id: sessionHistoryId || undefined,
            invoice_ids: invoiceIdsArray || undefined,
            save_card: payload.save_card,

            payment_card_id: !isNewCard ? payload.payment_card_id : undefined,

            ...(isNewCard && opaqueData
                ? {
                      tokenized_payment: {
                          opaque_data: {
                              dataDescriptor: opaqueData.dataDescriptor,
                              dataValue: opaqueData.dataValue,
                          },
                          billing_info: {
                              first_name: extractFirstName(
                                  payload.payment_card?.cardholder_name
                              ),
                              last_name: extractLastName(
                                  payload.payment_card?.cardholder_name
                              ),
                              billing_address:
                                  payload.payment_card?.billing_address || "",
                              state: payload.payment_card?.state || "",
                              zip_code: payload.payment_card?.zip_code || "",
                              country: clientCountry,
                          },
                      },
                  }
                : {}),
        };

        chargeCard(paymentData, {
            onSuccess: (res) => {
                queryClient.invalidateQueries({
                    queryKey: ["upcoming-sessions"],
                });
                queryClient.invalidateQueries({
                    queryKey: ["outstanding-bills"],
                });
                queryClient.invalidateQueries({ queryKey: ["payment-cards"] });
                toast({
                    mode: "success",
                    message: res.message || "Payment successful.",
                });
                navigate("/");
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not process payment at this time.",
                });
            },
        });
    };

    // Function to determine credit card provider icon to display
    const getCreditCardIconToDisplay = (cardType: string) => {
        switch (cardType) {
            case CardTypes.AMERICAN_EXPRESS:
                return cardImages.amex;

            case CardTypes.MASTERCARD:
                return cardImages.mastercard;

            case CardTypes.DISCOVER_FINANCIAL:
                return cardImages.discover;

            case CardTypes.CHASE:
                return cardImages.chase;

            case CardTypes.JCB:
                return cardImages.jcb;

            case CardTypes.CAPITAL_ONE:
                return cardImages.capitalone;

            case CardTypes.VISA:
                return cardImages.visa;

            case CardTypes.DINERSCLUB:
                return cardImages.dinersclub;

            default:
                return cardImages.capitalone;
        }
    };

    const getCardSourceBadge = (createdSource?: CreatedSource) => {
        if (!createdSource) {
            return <span className="text-xs font-medium px-2 py-1">--</span>;
        }

        if (createdSource === CreatedSource.PATIENT_PORTAL) {
            return (
                <Badge
                    content="Added by you"
                    bgColor="#e0f2fe"
                    textColor="#0284c7"
                />
            );
        }

        return (
            <Badge content="On file" bgColor="#f3f4f6" textColor="#6b7280" />
        );
    };
    const summaryLoading = invoicesLoading || sessionsLoading;
    const amountToPay = selectedSession?.fee || totalAmount;

    const hasSessionHistory = sessionHistoryId && selectedSession;

    const showSessionSummary =
        !summaryLoading && sessionsData && hasSessionHistory;
    const showInvoiceSummary =
        !summaryLoading && invoicesData && !hasSessionHistory;

    const provider = selectedSession?.provider;
    const supervisor = selectedSession?.supervisor;

    const sessionMessage = `Pay for your session with ${provider?.first_name} ${provider?.last_name}${
        supervisor
            ? ` (Supervisor - ${supervisor.first_name} ${supervisor.last_name})`
            : ""
    }`;

    const handleSwitchButtonClick = (tab: string) => {
        searchParams.set("payment_method", tab);
        setSearchParams(searchParams);
    };

    const switchOptions = [
        {
            title: "Saved cards",
            key: "saved_cards",
            onClick: () => handleSwitchButtonClick("saved_cards"),
        },
        {
            title: "New card",
            key: "new_card",
            onClick: () => handleSwitchButtonClick("new_card"),
        },
    ];

    const shouldShowPayButton = !(
        data?.data.length === 0 && paymentMethodInUrl === "saved_cards"
    );

    return (
        <div className="page">
            <BackLink
                to={sessionHistoryId ? "/" : "/billing"}
                buttonClass="mb-12"
            />
            {sessionHistoryId && selectedSession?.is_paid ? (
                <Alert
                    type="info"
                    description="Payment already processed for this session."
                    classNames="mb-12"
                />
            ) : null}
            <form id="payment-form" onSubmit={handleSubmit(onSubmit)}>
                <Card classNames="p-0">
                    <div className=" py-24 px-32 border-b border-b-strokedark ">
                        <p className="text-xl font-medium">
                            {showSessionSummary
                                ? sessionMessage
                                : "Pay your outstanding bills"}
                        </p>
                    </div>

                    <div className="flex flex-col xl:flex-row justify-between gap-x-6 gap-y-6 items-stretch min-h-[400px]">
                        <div className="bg-[rgba(241,_241,_243,_0.40)] rounded-r12 ml-12 mt-12 px-12 py-16 xl:w-1/2 flex-1">
                            <div className="bg-white rounded-r12">
                                <p className="text-base font-medium p-16 md:p-24 border-b border-b-strokelight">
                                    Service Details
                                </p>
                                {summaryLoading ? (
                                    <Skeleton
                                        count={3}
                                        width="100%"
                                        height="40px"
                                    />
                                ) : null}

                                {showSessionSummary && (
                                    <div className="p-16 md:p-24">
                                        <div className="mb-[80px]">
                                            <div className="grid grid-cols-3 gap-12 bg-blue-100 px-12 py-8 font-medium text-sm">
                                                <div>Date</div>
                                                <div>Service</div>
                                                <div>Amount</div>
                                            </div>
                                            <div className="grid grid-cols-3 gap-12 px-12 py-8 border-b border-b-strokedark text-sm">
                                                <div className="text-secondary-sub">
                                                    {formatDate(
                                                        selectedSession.start_date_time
                                                    )}
                                                </div>
                                                <div className="capitalize">
                                                    {removeEnumUnderscore(
                                                        selectedSession.service
                                                    )}
                                                </div>
                                                <div>
                                                    {showMoneyInAppFormat(
                                                        Math.abs(
                                                            selectedSession.fee
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex justify-between p-16 text-base font-medium bg-blue-100 rounded-r8 ">
                                            Total Amount:
                                            <p>
                                                {showMoneyInAppFormat(
                                                    Math.abs(
                                                        selectedSession.fee
                                                    )
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {showInvoiceSummary && (
                                    <div className="p-16 md:p-24 text-xs sm:text-sm">
                                        <div className="mb-[80px]">
                                            <div className="grid grid-cols-3 gap-12 bg-blue-100 px-12 py-8 font-medium">
                                                <div>Date</div>
                                                <div>Service</div>
                                                <div>Amount</div>
                                            </div>
                                            {selectedInvoices.map((summary) => (
                                                <div
                                                    key={summary.invoice_id}
                                                    className="grid grid-cols-3 gap-12 px-12 py-8 border-b border-b-strokedark"
                                                >
                                                    <div className="text-secondary-sub">
                                                        {formatDate(
                                                            summary.date_of_service
                                                        )}
                                                    </div>
                                                    <div className="capitalize">
                                                        {removeEnumUnderscore(
                                                            summary.service
                                                        )}
                                                    </div>
                                                    <div>
                                                        {showMoneyInAppFormat(
                                                            (() => {
                                                                const sessionFee =
                                                                    Math.abs(
                                                                        summary.coinsurance_amount ||
                                                                            0
                                                                    ) -
                                                                    Math.abs(
                                                                        summary.coinsurance_amount_collected ||
                                                                            0
                                                                    );

                                                                const insuranceReimbursement =
                                                                    Math.abs(
                                                                        summary.pr100_amount ||
                                                                            0
                                                                    ) -
                                                                    Math.abs(
                                                                        summary.pr100_collected ||
                                                                            0
                                                                    );

                                                                const adjustedSessionFee =
                                                                    sessionFee <
                                                                    0
                                                                        ? 0
                                                                        : sessionFee;
                                                                const adjustedInsuranceReimbursement =
                                                                    insuranceReimbursement <
                                                                    0
                                                                        ? 0
                                                                        : insuranceReimbursement;

                                                                return (
                                                                    adjustedSessionFee +
                                                                    adjustedInsuranceReimbursement
                                                                );
                                                            })()
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="flex justify-between p-16 text-sm sm:text-base font-medium bg-blue-100 rounded-r8 ">
                                            Total Amount:
                                            <p>
                                                {showMoneyInAppFormat(
                                                    totalAmount
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="border-t border-t-strokedark md:border-l md:border-l-strokedark md:border-t-0 p-16 xl:w-1/2 flex-1">
                            <Switch
                                switchOptions={switchOptions}
                                activeSwitch={paymentMethodInUrl}
                            />

                            <div className="mt-24">
                                {paymentMethodInUrl === "saved_cards" && (
                                    <>
                                        <p className=" text-sm text-[#5A5F6A] font-medium mb-8">
                                            Saved cards
                                        </p>
                                        {isLoading ? (
                                            <Skeleton
                                                count={3}
                                                width="100%"
                                                height="40px"
                                                containerTestId="card-loader"
                                            />
                                        ) : null}

                                        {error && (
                                            <ListState
                                                isError
                                                stateHelperText="Try reloading this page to solve this issue"
                                                errorMsg={
                                                    error?.response?.data
                                                        .message ||
                                                    `Cannot display saved cards at this time. Please try again later`
                                                }
                                            />
                                        )}

                                        {data?.data && (
                                            <>
                                                {data.warning && (
                                                    <Alert
                                                        type="warning"
                                                        description={
                                                            data.warning
                                                        }
                                                        classNames="mb-12"
                                                    />
                                                )}
                                                {data.data.length === 0 && (
                                                    <ListState
                                                        isError={false}
                                                        emptyMessage="No saved cards"
                                                        stateHelperText="Saved cards will appear here when available"
                                                    />
                                                )}

                                                {data.data.length > 0 && (
                                                    <>
                                                        <div className="flex flex-col gap-y-8 border border-strokedark py-8 rounded-r6">
                                                            {data.data.map(
                                                                (card) => {
                                                                    return (
                                                                        <div
                                                                            key={
                                                                                card.profile_id
                                                                            }
                                                                            className="flex gap-x-8 justify-between items-center px-8 py-12 [&:not(:last-child)]:border-b [&:not(:last-child)]:border-b-strokedark"
                                                                        >
                                                                            <div className="flex items-center gap-x-32">
                                                                                <div className=" border border-strokedark p-12 rounded-r6">
                                                                                    {getCreditCardIconToDisplay(
                                                                                        card.card_type
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex items-center gap-x-12">
                                                                                    <p className="text-sm font-medium">
                                                                                        {`${card.card_type} - ${card.masked_card_number}`}
                                                                                    </p>
                                                                                    {getCardSourceBadge(
                                                                                        card.created_source
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            <RadioInput
                                                                                {...register(
                                                                                    "payment_card_id"
                                                                                )}
                                                                                value={
                                                                                    card.payment_card_id
                                                                                }
                                                                                defaultChecked={
                                                                                    card.is_current ||
                                                                                    data
                                                                                        .data[0]
                                                                                        .payment_card_id ===
                                                                                        card.payment_card_id
                                                                                }
                                                                                label=""
                                                                                wrapperClass="border-none"
                                                                            />
                                                                        </div>
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                        {errors.payment_card_id ? (
                                                            <p className="text-sm text-danger mt-12">
                                                                Payment card is
                                                                required
                                                            </p>
                                                        ) : null}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                                {paymentMethodInUrl === "new_card" && (
                                    <>
                                        <div className="fg">
                                            <Input
                                                {...register(
                                                    "payment_card.cardholder_name"
                                                )}
                                                label="Cardholder Name"
                                                placeholder="Name on card"
                                                hasError={
                                                    !!errors.payment_card
                                                        ?.cardholder_name
                                                }
                                                errorText={
                                                    errors.payment_card
                                                        ?.cardholder_name
                                                        ?.message
                                                }
                                                className="fs-exclude"
                                                isRequired
                                            />
                                        </div>
                                        <div className="fg">
                                            <Input
                                                {...register(
                                                    "payment_card.card_number"
                                                )}
                                                label="Card number"
                                                placeholder="Card number"
                                                hasError={
                                                    !!errors.payment_card
                                                        ?.card_number
                                                }
                                                errorText={
                                                    errors.payment_card
                                                        ?.card_number?.message
                                                }
                                                className="fs-exclude"
                                                isRequired
                                            />
                                        </div>
                                        <div className="fg">
                                            <Input
                                                {...register(
                                                    "payment_card.expiration_date"
                                                )}
                                                label="Expiry Date"
                                                placeholder="Expiration date(mm/yy)"
                                                maxLength={5}
                                                hasError={
                                                    !!errors.payment_card
                                                        ?.expiration_date
                                                }
                                                errorText={
                                                    errors.payment_card
                                                        ?.expiration_date
                                                        ?.message
                                                }
                                                className="fs-exclude"
                                                isRequired
                                            />
                                        </div>
                                        <div className="fg">
                                            <Input
                                                {...register(
                                                    "payment_card.card_cvv"
                                                )}
                                                label="Security code (CVV)"
                                                placeholder="Security code (CVV)"
                                                hasError={
                                                    !!errors.payment_card
                                                        ?.card_cvv
                                                }
                                                errorText={
                                                    errors.payment_card
                                                        ?.card_cvv?.message
                                                }
                                                className="fs-exclude"
                                                isRequired
                                            />
                                        </div>
                                        <div className="fg">
                                            <Input
                                                {...register(
                                                    "payment_card.billing_address"
                                                )}
                                                label="Billing address (please include city)"
                                                placeholder="Billing address (please include city)"
                                                hasError={
                                                    !!errors.payment_card
                                                        ?.billing_address
                                                }
                                                errorText={
                                                    errors.payment_card
                                                        ?.billing_address
                                                        ?.message
                                                }
                                                className="fs-exclude"
                                                isRequired
                                            />
                                        </div>
                                        <div className="fg">
                                            <Input
                                                {...register(
                                                    "payment_card.zip_code"
                                                )}
                                                label="Zip code"
                                                placeholder="Zip code"
                                                hasError={
                                                    !!errors.payment_card
                                                        ?.zip_code
                                                }
                                                errorText={
                                                    errors.payment_card
                                                        ?.zip_code?.message
                                                }
                                                className="fs-exclude"
                                                isRequired
                                            />
                                        </div>
                                        <div
                                            className="fg fg-space-between two flex fs-exclude

"
                                        >
                                            <Controller
                                                name="payment_card.state"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        label="State"
                                                        options={
                                                            getClientStateFieldSelectOptions
                                                        }
                                                        onChange={(val) =>
                                                            field.onChange(
                                                                (val as Option)
                                                                    .value
                                                            )
                                                        }
                                                        isSearchable
                                                        hasError={
                                                            !!errors
                                                                .payment_card
                                                                ?.state
                                                        }
                                                        errorText={
                                                            errors.payment_card
                                                                ?.state?.message
                                                        }
                                                        isLongListInDialog
                                                        isRequired
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name="payment_card.country"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        label="Country"
                                                        options={[
                                                            {
                                                                label: "United States",
                                                                value: "USA",
                                                            },
                                                        ]}
                                                        onChange={(val) =>
                                                            field.onChange(
                                                                (val as Option)
                                                                    .value
                                                            )
                                                        }
                                                        isSearchable
                                                        hasError={
                                                            !!errors
                                                                .payment_card
                                                                ?.country
                                                        }
                                                        errorText={
                                                            errors.payment_card
                                                                ?.country
                                                                ?.message
                                                        }
                                                        defaultValue={{
                                                            label: "United States",
                                                            value: "USA",
                                                        }}
                                                        isRequired
                                                    />
                                                )}
                                            />
                                        </div>

                                        <FormCheckbox
                                            {...register("save_card")}
                                            value="true"
                                            labelClass="font-semibold text-sm text-black"
                                            label=" Save this card for future payments"
                                        />
                                    </>
                                )}
                            </div>

                            {shouldShowPayButton && (
                                <Button
                                    size="medium"
                                    type="submit"
                                    form="payment-form"
                                    disabled={
                                        chargeCardLoading ||
                                        (paymentMethodInUrl === "new_card" &&
                                            !isReady) ||
                                        (!!sessionHistoryId &&
                                            selectedSession?.is_paid)
                                    }
                                    className="mt-24 w-full"
                                >
                                    Pay {showMoneyInAppFormat(amountToPay)}
                                </Button>
                            )}
                        </div>
                    </div>
                </Card>
            </form>
        </div>
    );
}
